import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { CalendarIcon, Flag, Award, School, Dumbbell } from "lucide-react";

// Schema for validation
const milestoneFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  date: z.date(),
  type: z.string().optional(),
  isHighlight: z.boolean().default(false),
});

type MilestoneFormValues = z.infer<typeof milestoneFormSchema>;

interface AddMilestoneFormProps {
  athleteId?: string;
  onSuccess?: () => void;
  milestoneToEdit?: any;
}

const AddMilestoneForm = ({ athleteId, onSuccess, milestoneToEdit }: AddMilestoneFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Set up form
  const form = useForm<MilestoneFormValues>({
    resolver: zodResolver(milestoneFormSchema),
    defaultValues: {
      title: milestoneToEdit?.title || "",
      description: milestoneToEdit?.description || "",
      date: milestoneToEdit?.date ? new Date(milestoneToEdit.date) : new Date(),
      type: milestoneToEdit?.type || "achievement",
      isHighlight: milestoneToEdit?.isHighlight || false,
    },
  });

  // Create/Update milestone mutation
  const milestoneMutation = useMutation({
    mutationFn: async (values: MilestoneFormValues) => {
      if (milestoneToEdit) {
        // Update existing milestone
        return apiRequest('PUT', `/api/milestones/${milestoneToEdit.id}`, values);
      } else {
        // Create new milestone
        return apiRequest('POST', `/api/athletes/${athleteId}/milestones`, values);
      }
    },
    onSuccess: () => {
      toast({
        title: milestoneToEdit ? "Milestone Updated" : "Milestone Added",
        description: milestoneToEdit 
          ? "Milestone has been updated successfully" 
          : "New milestone has been added to your journey"
      });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes", athleteId, "milestones"] });
      if (onSuccess) onSuccess();
    },
    onError: () => {
      toast({
        title: "Error",
        description: milestoneToEdit 
          ? "Failed to update milestone" 
          : "Failed to add milestone",
        variant: "destructive"
      });
    }
  });

  // Form submission handler
  const onSubmit = (values: MilestoneFormValues) => {
    if (!athleteId && !milestoneToEdit) {
      toast({
        title: "Error",
        description: "Athlete information is missing",
        variant: "destructive"
      });
      return;
    }
    
    milestoneMutation.mutate(values);
  };

  // Get icon for milestone type
  const getMilestoneTypeIcon = (type: string) => {
    switch (type) {
      case "achievement":
        return <Award className="h-4 w-4" />;
      case "recruiting":
        return <School className="h-4 w-4" />;
      case "training":
        return <Dumbbell className="h-4 w-4" />;
      default:
        return <Flag className="h-4 w-4" />;
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <h2 className="text-xl font-bold">
          {milestoneToEdit ? "Edit Milestone" : "Add New Milestone"}
        </h2>
        
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Milestone title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Date</FormLabel>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className="w-full pl-3 text-left font-normal"
                      >
                        {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(d) => { field.onChange(d); setDatePickerOpen(false); }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select milestone type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="achievement">
                      <div className="flex items-center">
                        <Award className="h-4 w-4 mr-2 text-secondary" />
                        <span>Achievement</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="recruiting">
                      <div className="flex items-center">
                        <School className="h-4 w-4 mr-2 text-accent" />
                        <span>Recruiting</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="training">
                      <div className="flex items-center">
                        <Dumbbell className="h-4 w-4 mr-2 text-primary" />
                        <span>Training</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="other">
                      <div className="flex items-center">
                        <Flag className="h-4 w-4 mr-2 text-neutral-500" />
                        <span>Other</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe this milestone and why it's important..." 
                  rows={4}
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="isHighlight"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel className="flex items-center">
                  <Award className="h-4 w-4 mr-1 text-secondary" />
                  Mark as Journey Highlight
                </FormLabel>
                <FormDescription>
                  Highlight important milestones in your timeline
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <div className="flex justify-end space-x-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (onSuccess) onSuccess();
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={milestoneMutation.isPending}
            className="bg-primary"
          >
            {milestoneMutation.isPending 
              ? (milestoneToEdit ? "Updating..." : "Adding...") 
              : (milestoneToEdit ? "Update Milestone" : "Add Milestone")
            }
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AddMilestoneForm;
